import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';

type HubKind = 'why' | 'products' | 'pricing';
type ProductGroup = {
  key: string;
  number: string;
  title: string;
  lead: string;
  items: Array<{ title: string; description: string }>;
};

@Component({
  selector: 'app-aymudo-marketing-hub-page',
  imports: [RouterLink],
  template: `
    <main class="marketing-hub" [attr.data-kind]="kind()">
      <section class="hub-hero">
        <div class="hub-path" aria-hidden="true"><i></i><i></i><i></i><i></i></div>
        <p>{{ copy().eyebrow }}</p>
        <h1>{{ copy().title }}</h1>
        <span>{{ copy().lead }}</span>
        <div class="hub-actions">
          <a class="primary" [routerLink]="i18n.routePath('subscribe')"
            >{{ copy().subscribe }} <b>↗</b></a
          >
          <a [routerLink]="i18n.routePath(kind() === 'pricing' ? 'products' : 'pricing')"
            >{{ copy().secondary }} <b>→</b></a
          >
        </div>
      </section>

      @if (kind() === 'products') {
        <nav class="group-index" aria-label="Ürün grupları">
          @for (group of productGroups(); track group.key) {
            <a [href]="'#' + group.key"
              ><span>{{ group.number }}</span
              >{{ group.title }}</a
            >
          }
        </nav>
        <section class="product-groups">
          @for (group of productGroups(); track group.key; let groupIndex = $index) {
            <article class="product-group" [id]="group.key">
              <header>
                <span>{{ group.number }}</span>
                <div>
                  <h2>{{ group.title }}</h2>
                  <p>{{ group.lead }}</p>
                </div>
              </header>
              <div class="product-links">
                @for (item of group.items; track item.title; let itemIndex = $index) {
                  <a [routerLink]="i18n.localizedPath(productPath(groupIndex, itemIndex))">
                    <strong>{{ item.title }}</strong
                    ><small>{{ item.description }}</small
                    ><b>↗</b>
                  </a>
                }
              </div>
            </article>
          }
        </section>
      } @else if (kind() === 'why') {
        <section class="promise-grid">
          @for (item of whyItems(); track item.title; let index = $index) {
            <article>
              <span>0{{ index + 1 }}</span>
              <h2>{{ item.title }}</h2>
              <p>{{ item.text }}</p>
            </article>
          }
        </section>
        <section class="system-statement">
          <p>{{ copy().statementEyebrow }}</p>
          <h2>{{ copy().statement }}</h2>
          <div>
            @for (verb of verbs(); track verb; let last = $last) {
              <span>{{ verb }}</span>
              @if (!last) {
                <i></i>
              }
            }
          </div>
        </section>
      } @else {
        <section class="pricing-principles">
          @for (item of pricingItems(); track item.title; let index = $index) {
            <article>
              <span>0{{ index + 1 }}</span>
              <h2>{{ item.title }}</h2>
              <p>{{ item.text }}</p>
              <a [routerLink]="i18n.routePath('subscribe')">{{ copy().subscribe }} →</a>
            </article>
          }
        </section>
      }

      <section class="hub-depth">
        <header>
          <p>{{ depth().eyebrow }}</p>
          <h2>{{ depth().title }}</h2>
          <span>{{ depth().lead }}</span>
        </header>
        <div class="depth-grid">
          @for (item of depth().sections; track item.title; let index = $index) {
            <article>
              <small>0{{ index + 1 }}</small>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
              <ul>
                @for (point of item.points; track point) {
                  <li>{{ point }}</li>
                }
              </ul>
            </article>
          }
        </div>
      </section>

      <section class="hub-faq">
        <header>
          <p>{{ depth().faqEyebrow }}</p>
          <h2>{{ depth().faqTitle }}</h2>
        </header>
        <div>
          @for (item of depth().faqs; track item.question; let index = $index) {
            <details>
              <summary>
                <span>0{{ index + 1 }}</span>
                <strong>{{ item.question }}</strong>
                <b aria-hidden="true">+</b>
              </summary>
              <p>{{ item.answer }}</p>
            </details>
          }
        </div>
      </section>

      <section class="hub-subscribe">
        <p>{{ copy().closeEyebrow }}</p>
        <h2>{{ copy().closeTitle }}</h2>
        <a [routerLink]="i18n.routePath('subscribe')">{{ copy().subscribe }} <b>↗</b></a>
      </section>
    </main>
  `,
  styleUrl: './aymudo-marketing-hub.page.scss',
})
export class AymudoMarketingHubPage {
  protected readonly i18n = inject(I18nService);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly routeData = toSignal(this.route.data, {
    initialValue: this.route.snapshot.data,
  });
  protected readonly kind = computed(() => (this.routeData()['hubKind'] || 'products') as HubKind);
  protected readonly copy = computed(() => {
    const lang = this.i18n.contentLang();
    const kind = this.kind();
    if (HUB_COPY[lang]?.[kind]) return HUB_COPY[lang][kind];
    const localized = HUB_LOCALIZED[lang];
    return { ...HUB_COPY['en'][kind], ...(localized?.common ?? {}), ...(localized?.[kind] ?? {}) };
  });
  protected readonly verbs = computed(() => VERBS[this.i18n.contentLang()] ?? VERBS['en']);
  protected readonly productGroups = computed(
    () => PRODUCT_GROUPS[this.i18n.contentLang()] ?? PRODUCT_GROUPS['en'],
  );
  protected readonly whyItems = computed(
    () => WHY_ITEMS[this.i18n.contentLang()] ?? WHY_ITEMS['en'],
  );
  protected readonly pricingItems = computed(
    () => PRICING_ITEMS[this.i18n.contentLang()] ?? PRICING_ITEMS['en'],
  );
  protected readonly depth = computed(() => {
    const language = this.i18n.contentLang();
    return HUB_DEPTH[language]?.[this.kind()] ?? HUB_DEPTH['en'][this.kind()];
  });
  protected productPath(groupIndex: number, itemIndex: number): string {
    return PRODUCT_PATHS[groupIndex]?.[itemIndex] ?? '/urunler';
  }
  constructor() {
    effect(() => {
      const copy = this.copy();
      this.seo.setPage({
        title: copy.seoTitle,
        description: copy.lead,
        path: this.i18n.routePath(this.kind() === 'why' ? 'whyAymudo' : this.kind()),
      });
    });
  }
}

const PRODUCT_PATHS = [
  [
    '/urunler/web-sitesi',
    '/urunler/tema-motoru',
    '/urunler/icerik-yayin',
    '/urunler/yerellestirme',
  ],
  ['/urunler/randevu', '/urunler/sube-personel', '/urunler/ekran-yetki', '/urunler/mailbox'],
  ['/urunler/e-ticaret', '/urunler/stok-depo', '/urunler/pos-kasa', '/urunler/pazaryeri'],
  [
    '/urunler/kampanya-reklam',
    '/urunler/sosyal-medya',
    '/urunler/tetikleyiciler',
    '/urunler/global-satis',
  ],
];

type HubCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  subscribe: string;
  secondary: string;
  statementEyebrow: string;
  statement: string;
  closeEyebrow: string;
  closeTitle: string;
  seoTitle: string;
};
const HUB_COPY: Record<string, Record<HubKind, HubCopy>> = {
  tr: {
    why: {
      eyebrow: 'Neden Aymudo',
      title: 'Ayrı araçlar değil, birlikte çalışan bir işletme.',
      lead: 'Markanızdan ekibinize, randevudan satışa kadar işinizin parçaları aynı sistemde birbirini tamamlar.',
      subscribe: 'Aymudo’ya abone ol',
      secondary: 'Ürünleri incele',
      statementEyebrow: 'Aymudo yaklaşımı',
      statement: 'İşletmeniz değişirken sisteminiz geride kalmaz.',
      closeEyebrow: 'Birlikte çalışmaya başlayın',
      closeTitle: 'İşletmenizin arkasındaki sistemi kurun.',
      seoTitle: 'Neden Aymudo? | Birlikte çalışan işletme sistemi',
    },
    products: {
      eyebrow: 'Aymudo ürünleri',
      title: 'İşletmenizin tamamı için birbiriyle konuşan ürünler.',
      lead: 'İhtiyacınız olan yerden başlayın. Yeni şube, kanal, ekip veya ülke eklendikçe aynı sistem üzerinde genişleyin.',
      subscribe: 'Aymudo’ya abone ol',
      secondary: 'Fiyatlandırmayı gör',
      statementEyebrow: 'Tek altyapı',
      statement: 'Kurun, yönetin, satın ve büyütün.',
      closeEyebrow: 'İhtiyacınız olan yerden başlayın',
      closeTitle: 'Bugünkü işinize uyan, yarınınıza yetişen sistem.',
      seoTitle: 'Aymudo Ürünleri | İşletmeni kur, yönet, sat ve büyüt',
    },
    pricing: {
      eyebrow: 'Fiyatlandırma',
      title: 'Kullanmadığınız karmaşaya değil, işinize gereken sisteme abone olun.',
      lead: 'İş modelinizi ve ihtiyaç duyduğunuz ürünleri seçin. Aymudo büyüdükçe genişleyebileceğiniz anlaşılır bir başlangıç oluşturur.',
      subscribe: 'Abone ol',
      secondary: 'Ürünleri incele',
      statementEyebrow: 'Esnek başlangıç',
      statement: 'İşiniz büyüdüğünde sistem değiştirmeyin.',
      closeEyebrow: 'Planınızı oluşturun',
      closeTitle: 'Aymudo ile çalışmaya başlayın.',
      seoTitle: 'Aymudo Fiyatlandırma | İşletmenize uygun abonelik',
    },
  },
  en: {
    why: {
      eyebrow: 'Why Aymudo',
      title: 'Not separate tools. One business working together.',
      lead: 'From your brand and team to bookings and sales, every part of the business completes the next in one system.',
      subscribe: 'Subscribe to Aymudo',
      secondary: 'Explore products',
      statementEyebrow: 'The Aymudo approach',
      statement: 'As your business changes, your system does not fall behind.',
      closeEyebrow: 'Start working as one',
      closeTitle: 'Build the system behind your business.',
      seoTitle: 'Why Aymudo? | One connected business system',
    },
    products: {
      eyebrow: 'Aymudo products',
      title: 'Connected products for the whole business.',
      lead: 'Start where you need. Add a branch, channel, team or country without replacing the system beneath your work.',
      subscribe: 'Subscribe to Aymudo',
      secondary: 'See pricing',
      statementEyebrow: 'One infrastructure',
      statement: 'Build, manage, sell and grow.',
      closeEyebrow: 'Start where you need',
      closeTitle: 'A system for today that is ready for tomorrow.',
      seoTitle: 'Aymudo Products | Build, manage, sell and grow',
    },
    pricing: {
      eyebrow: 'Pricing',
      title: 'Subscribe to what your business needs, not unused complexity.',
      lead: 'Choose your business model and the products you need. Start clearly and expand on the same system as you grow.',
      subscribe: 'Subscribe',
      secondary: 'Explore products',
      statementEyebrow: 'Flexible start',
      statement: 'Do not replace the system when the business grows.',
      closeEyebrow: 'Build your plan',
      closeTitle: 'Start working with Aymudo.',
      seoTitle: 'Aymudo Pricing | A subscription for your business',
    },
  },
};

type HubLocalized = {
  common?: Partial<HubCopy>;
  why?: Partial<HubCopy>;
  products?: Partial<HubCopy>;
  pricing?: Partial<HubCopy>;
};
const HUB_LOCALIZED: Record<string, HubLocalized> = {
  de: {
    common: { subscribe: 'Aymudo abonnieren' },
    why: {
      title: 'Keine getrennten Tools. Ein Unternehmen, das zusammenarbeitet.',
      lead: 'Von Marke und Team bis zu Terminen und Verkäufen ergänzt jeder Teil den nächsten.',
    },
    products: {
      title: 'Vernetzte Produkte für das ganze Unternehmen.',
      lead: 'Starten Sie nach Bedarf und erweitern Sie Filialen, Kanäle, Teams oder Länder im selben System.',
      secondary: 'Preise ansehen',
    },
    pricing: {
      title: 'Abonnieren Sie, was Ihr Unternehmen wirklich braucht.',
      lead: 'Wählen Sie Ihr Geschäftsmodell und die passenden Produkte. Erweitern Sie später im selben System.',
      secondary: 'Produkte entdecken',
    },
  },
  es: {
    common: { subscribe: 'Suscribirse a Aymudo' },
    why: {
      title: 'No son herramientas separadas. Es un negocio que funciona unido.',
      lead: 'Desde la marca y el equipo hasta las citas y ventas, cada parte completa a la siguiente.',
    },
    products: {
      title: 'Productos conectados para todo el negocio.',
      lead: 'Empieza donde lo necesites y añade sucursales, canales, equipos o países sin cambiar de sistema.',
      secondary: 'Ver precios',
    },
    pricing: {
      title: 'Suscríbete a lo que tu negocio necesita, no a complejidad innecesaria.',
      lead: 'Elige tu modelo y los productos que necesitas. Amplía el mismo sistema cuando crezcas.',
      secondary: 'Explorar productos',
    },
  },
  fr: {
    common: { subscribe: 'S’abonner à Aymudo' },
    why: {
      title: 'Pas d’outils séparés. Une entreprise qui fonctionne ensemble.',
      lead: 'De la marque et l’équipe aux rendez-vous et ventes, chaque partie complète la suivante.',
    },
    products: {
      title: 'Des produits connectés pour toute l’entreprise.',
      lead: 'Commencez où vous voulez et ajoutez sites, canaux, équipes ou pays sans changer de système.',
      secondary: 'Voir les tarifs',
    },
    pricing: {
      title: 'Abonnez-vous à ce dont votre entreprise a réellement besoin.',
      lead: 'Choisissez votre modèle et vos produits, puis évoluez sur le même système.',
      secondary: 'Découvrir les produits',
    },
  },
  ar: {
    common: { subscribe: 'اشترك في أيمودو' },
    why: {
      title: 'ليست أدوات منفصلة، بل عمل واحد يعمل معاً.',
      lead: 'من العلامة والفريق إلى المواعيد والمبيعات، يكمل كل جزء ما يليه.',
    },
    products: {
      title: 'منتجات مترابطة لأعمالك بالكامل.',
      lead: 'ابدأ من حاجتك وأضف فرعاً أو قناة أو فريقاً أو دولة دون استبدال النظام.',
      secondary: 'عرض الأسعار',
    },
    pricing: {
      title: 'اشترك فيما يحتاجه عملك، لا في تعقيد لا تستخدمه.',
      lead: 'اختر نموذج عملك والمنتجات المطلوبة ثم توسع على النظام نفسه.',
      secondary: 'استكشف المنتجات',
    },
  },
  pt: {
    common: { subscribe: 'Assinar Aymudo' },
    why: {
      title: 'Não são ferramentas separadas. É um negócio trabalhando em conjunto.',
      lead: 'Da marca e equipe aos agendamentos e vendas, cada parte completa a próxima.',
    },
    products: {
      title: 'Produtos conectados para todo o negócio.',
      lead: 'Comece onde precisa e adicione filial, canal, equipe ou país sem trocar o sistema.',
      secondary: 'Ver preços',
    },
    pricing: {
      title: 'Assine o que sua empresa precisa, não complexidade sem uso.',
      lead: 'Escolha o modelo e os produtos necessários e expanda no mesmo sistema.',
      secondary: 'Explorar produtos',
    },
  },
  ru: {
    common: { subscribe: 'Подписаться на Aymudo' },
    why: {
      title: 'Не отдельные инструменты, а единый работающий бизнес.',
      lead: 'От бренда и команды до записей и продаж — каждая часть дополняет следующую.',
    },
    products: {
      title: 'Связанные продукты для всего бизнеса.',
      lead: 'Начните с нужного и добавляйте филиалы, каналы, команды или страны без смены системы.',
      secondary: 'Смотреть тарифы',
    },
    pricing: {
      title: 'Подписывайтесь на нужную бизнесу систему, а не лишнюю сложность.',
      lead: 'Выберите модель и продукты, затем расширяйтесь в той же системе.',
      secondary: 'Изучить продукты',
    },
  },
  zh: {
    common: { subscribe: '订阅 Aymudo' },
    why: {
      title: '不是割裂的工具，而是协同运行的企业。',
      lead: '从品牌和团队到预约与销售，每个环节都会衔接下一个环节。',
    },
    products: {
      title: '为整个企业打造的互联产品。',
      lead: '从所需之处开始，增加分支、渠道、团队或国家时无需更换系统。',
      secondary: '查看定价',
    },
    pricing: {
      title: '只订阅企业所需，不为无用的复杂度付费。',
      lead: '选择业务模式与所需产品，并在成长时基于同一系统扩展。',
      secondary: '探索产品',
    },
  },
  hi: {
    common: { subscribe: 'Aymudo की सदस्यता लें' },
    why: {
      title: 'अलग टूल नहीं। एक साथ काम करने वाला व्यवसाय।',
      lead: 'ब्रांड और टीम से लेकर अपॉइंटमेंट और बिक्री तक, हर हिस्सा अगले से जुड़ता है।',
    },
    products: {
      title: 'पूरे व्यवसाय के लिए जुड़े हुए उत्पाद।',
      lead: 'जहाँ जरूरत हो वहाँ से शुरू करें और सिस्टम बदले बिना शाखा, चैनल, टीम या देश जोड़ें।',
      secondary: 'मूल्य देखें',
    },
    pricing: {
      title: 'जिस सिस्टम की जरूरत है उसी की सदस्यता लें।',
      lead: 'अपना मॉडल और उत्पाद चुनें, फिर उसी सिस्टम पर विस्तार करें।',
      secondary: 'उत्पाद देखें',
    },
  },
  ja: {
    common: { subscribe: 'Aymudoを申し込む' },
    why: {
      title: '分断されたツールではなく、ひとつにつながるビジネス。',
      lead: 'ブランドとチームから予約と販売まで、すべてが次の仕事につながります。',
    },
    products: {
      title: '事業全体のための、つながる製品群。',
      lead: '必要な場所から始め、システムを変えずに拠点、チャネル、チーム、国を追加できます。',
      secondary: '料金を見る',
    },
    pricing: {
      title: '使わない複雑さではなく、必要な仕組みを選ぶ。',
      lead: '事業モデルと製品を選び、成長に合わせて同じシステムで拡張できます。',
      secondary: '製品を見る',
    },
  },
  ko: {
    common: { subscribe: 'Aymudo 구독하기' },
    why: {
      title: '분리된 도구가 아니라 함께 작동하는 하나의 비즈니스.',
      lead: '브랜드와 팀부터 예약과 판매까지 모든 요소가 다음 업무로 이어집니다.',
    },
    products: {
      title: '비즈니스 전체를 위한 연결된 제품.',
      lead: '필요한 곳에서 시작하고 시스템 교체 없이 지점, 채널, 팀 또는 국가를 추가하세요.',
      secondary: '요금 보기',
    },
    pricing: {
      title: '쓰지 않는 복잡함이 아니라 필요한 시스템을 구독하세요.',
      lead: '비즈니스 모델과 제품을 선택하고 같은 시스템에서 확장하세요.',
      secondary: '제품 살펴보기',
    },
  },
  it: {
    common: { subscribe: 'Abbonati ad Aymudo' },
    why: {
      title: 'Non strumenti separati. Un’impresa che lavora insieme.',
      lead: 'Dal brand e dal team agli appuntamenti e alle vendite, ogni parte completa la successiva.',
    },
    products: {
      title: 'Prodotti connessi per tutta l’impresa.',
      lead: 'Inizia da ciò che serve e aggiungi sedi, canali, team o paesi senza cambiare sistema.',
      secondary: 'Vedi i prezzi',
    },
    pricing: {
      title: 'Abbonati a ciò che serve, non a complessità inutilizzata.',
      lead: 'Scegli modello e prodotti, poi amplia lo stesso sistema mentre cresci.',
      secondary: 'Scopri i prodotti',
    },
  },
  nl: {
    common: { subscribe: 'Abonneer op Aymudo' },
    why: {
      title: 'Geen losse tools. Eén bedrijf dat samenwerkt.',
      lead: 'Van merk en team tot afspraken en verkoop vult elk onderdeel het volgende aan.',
    },
    products: {
      title: 'Verbonden producten voor het hele bedrijf.',
      lead: 'Begin waar nodig en voeg vestigingen, kanalen, teams of landen toe zonder het systeem te vervangen.',
      secondary: 'Bekijk prijzen',
    },
    pricing: {
      title: 'Abonneer op wat uw bedrijf nodig heeft, niet op ongebruikte complexiteit.',
      lead: 'Kies uw model en producten en breid later uit op hetzelfde systeem.',
      secondary: 'Ontdek producten',
    },
  },
  id: {
    common: { subscribe: 'Berlangganan Aymudo' },
    why: {
      title: 'Bukan alat terpisah. Satu bisnis yang bekerja bersama.',
      lead: 'Dari merek dan tim hingga janji temu dan penjualan, setiap bagian melengkapi berikutnya.',
    },
    products: {
      title: 'Produk terhubung untuk seluruh bisnis.',
      lead: 'Mulai dari yang dibutuhkan dan tambahkan cabang, kanal, tim, atau negara tanpa mengganti sistem.',
      secondary: 'Lihat harga',
    },
    pricing: {
      title: 'Berlangganan sesuai kebutuhan bisnis, bukan kerumitan yang tidak digunakan.',
      lead: 'Pilih model dan produk, lalu kembangkan pada sistem yang sama.',
      secondary: 'Jelajahi produk',
    },
  },
};

const VERBS: Record<string, string[]> = {
  tr: ['Kur', 'Yönet', 'Sat', 'Büyüt'],
  en: ['Build', 'Manage', 'Sell', 'Grow'],
  de: ['Aufbauen', 'Führen', 'Verkaufen', 'Wachsen'],
  es: ['Crear', 'Gestionar', 'Vender', 'Crecer'],
  fr: ['Créer', 'Gérer', 'Vendre', 'Grandir'],
  ar: ['أنشئ', 'أدر', 'بِع', 'نمِّ'],
  pt: ['Criar', 'Gerenciar', 'Vender', 'Crescer'],
  ru: ['Создать', 'Управлять', 'Продавать', 'Расти'],
  zh: ['创建', '管理', '销售', '成长'],
  hi: ['बनाएं', 'संभालें', 'बेचें', 'बढ़ें'],
  ja: ['作る', '管理する', '売る', '成長する'],
  ko: ['구축', '관리', '판매', '성장'],
  it: ['Crea', 'Gestisci', 'Vendi', 'Cresci'],
  nl: ['Bouwen', 'Beheren', 'Verkopen', 'Groeien'],
  id: ['Bangun', 'Kelola', 'Jual', 'Tumbuh'],
};

const PRODUCT_GROUPS: Record<string, ProductGroup[]> = {
  tr: [
    {
      key: 'kur',
      number: '01',
      title: 'İşletmeni kur',
      lead: 'Markanızın görünen yüzünü ve dijital temelini oluşturun.',
      items: [
        {
          title: 'Web sitesi ve yayın sistemi',
          description: 'Sayfalarınızı ve içeriğinizi tek merkezden yayınlayın.',
        },
        { title: 'Tema motoru', description: 'Aynı altyapıyı tamamen kendi markanıza dönüştürün.' },
        {
          title: 'İçerik ve blog',
          description: 'Hikâyenizi, rehberlerinizi ve güncellemelerinizi yönetin.',
        },
        {
          title: 'Dil ve ülke',
          description: 'Yerelleştirilmiş deneyimlerle farklı pazarlara hazırlanın.',
        },
      ],
    },
    {
      key: 'yonet',
      number: '02',
      title: 'İşletmeni yönet',
      lead: 'Ekip, şube, zaman ve iletişimi aynı çalışma alanında birleştirin.',
      items: [
        {
          title: 'Randevular',
          description: 'Hizmet, personel ve kaynak uygunluğunu birlikte yönetin.',
        },
        {
          title: 'Şubeler ve personeller',
          description: 'Birden fazla lokasyonu ve ekibi tek panelden çalıştırın.',
        },
        {
          title: 'Ekran ve yetki atama',
          description: 'Herkese yalnızca işi için gereken çalışma ekranını verin.',
        },
        {
          title: 'Mailbox',
          description: 'Müşteri ve ekip iletişimini ortak gelen kutusunda yönetin.',
        },
      ],
    },
    {
      key: 'sat',
      number: '03',
      title: 'Her yerde sat',
      lead: 'İnternette, sosyal medyada ve fiziksel noktada tek ticaret akışı kurun.',
      items: [
        {
          title: 'E-ticaret',
          description: 'Ürün, sepet, ödeme, kargo ve siparişi birlikte yönetin.',
        },
        {
          title: 'Stok ve depo',
          description: 'Ürünü, varyasyonu, depoyu ve barkodu aynı kayıtta izleyin.',
        },
        {
          title: 'Aymudo POS',
          description: 'Adisyon, kasa ve fiziksel satışı gerçek sipariş akışına bağlayın.',
        },
        {
          title: 'Pazaryeri',
          description: 'Satıcı, ürün, komisyon ve siparişi tek platformda çalıştırın.',
        },
      ],
    },
    {
      key: 'buyut',
      number: '04',
      title: 'İşletmeni büyüt',
      lead: 'Yeni müşterilere ulaşın ve tekrar eden işleri otomatikleştirin.',
      items: [
        {
          title: 'Kampanyalar ve reklamlar',
          description: 'Teklifleri ve büyüme çalışmalarını aynı merkezden yönetin.',
        },
        {
          title: 'Sosyal medya yönetimi',
          description: 'Gönderi, yorum ve müşteri konuşmalarını bir akışta toplayın.',
        },
        {
          title: 'Tetikleyiciler',
          description: 'Site ve operasyon genelinde koşula bağlı otomasyonlar kurun.',
        },
        {
          title: 'Global satış',
          description: '15 dil, RTL ve yaklaşık 30 para birimiyle dünyaya açılın.',
        },
      ],
    },
  ],
};
PRODUCT_GROUPS['en'] = [
  {
    key: 'build',
    number: '01',
    title: 'Build your business',
    lead: 'Create the visible brand and its digital foundation.',
    items: [
      { title: 'Website and publishing', description: 'Publish pages and content from one place.' },
      { title: 'Theme engine', description: 'Turn shared infrastructure into your own brand.' },
      { title: 'Content and blog', description: 'Manage stories, guides and updates.' },
      {
        title: 'Language and country',
        description: 'Prepare localised experiences for new markets.',
      },
    ],
  },
  {
    key: 'manage',
    number: '02',
    title: 'Manage your business',
    lead: 'Bring team, branches, time and communication together.',
    items: [
      {
        title: 'Appointments',
        description: 'Manage service, staff and resource availability together.',
      },
      {
        title: 'Branches and staff',
        description: 'Run multiple locations and teams from one panel.',
      },
      {
        title: 'Screens and permissions',
        description: 'Give each person the workspace their job needs.',
      },
      { title: 'Mailbox', description: 'Manage customer and team communication in one inbox.' },
    ],
  },
  {
    key: 'sell',
    number: '03',
    title: 'Sell everywhere',
    lead: 'Connect online, social and physical commerce.',
    items: [
      { title: 'Commerce', description: 'Manage product, cart, payment, shipping and order.' },
      {
        title: 'Inventory and warehouse',
        description: 'Track variants, warehouses and barcodes together.',
      },
      { title: 'Aymudo POS', description: 'Connect till, tab and physical sale to real orders.' },
      {
        title: 'Marketplace',
        description: 'Run sellers, products, commissions and orders together.',
      },
    ],
  },
  {
    key: 'grow',
    number: '04',
    title: 'Grow your business',
    lead: 'Reach new customers and automate repeated work.',
    items: [
      { title: 'Campaigns and ads', description: 'Manage offers and growth work from one centre.' },
      {
        title: 'Social management',
        description: 'Bring posts, comments and conversations into one flow.',
      },
      {
        title: 'Triggers',
        description: 'Build conditional automation across the site and operation.',
      },
      {
        title: 'Global selling',
        description: 'Reach the world with 15 languages, RTL and nearly 30 currencies.',
      },
    ],
  },
];

const WHY_ITEMS: Record<string, Array<{ title: string; text: string }>> = {
  tr: [
    { title: 'Tek merkez', text: 'Müşteri, ekip, ürün, randevu ve satış aynı veriyi kullanır.' },
    {
      title: 'İşinize göre',
      text: 'Hazır bir kalıba sıkışmaz; marka ve çalışma akışı işletmenize dönüşür.',
    },
    {
      title: 'Ekip olarak',
      text: 'Personeller yalnızca ihtiyaç duydukları ekran ve yetkilerle çalışır.',
    },
    {
      title: 'Her kanalda',
      text: 'Web, sosyal medya, pazaryeri ve fiziksel satış birbirinden kopmaz.',
    },
    {
      title: 'Büyümeye hazır',
      text: 'Yeni şube, depo, ekip, dil veya ülke aynı omurgaya eklenir.',
    },
    {
      title: 'Sürekli gelişen',
      text: 'Yeni yetenekler ayrı bir sistem kurmadan kullandığınız ürüne ulaşır.',
    },
  ],
  en: [
    {
      title: 'One centre',
      text: 'Customers, team, products, bookings and sales share the same data.',
    },
    {
      title: 'Made for your work',
      text: 'The brand and workflow adapt to the business instead of a fixed template.',
    },
    {
      title: 'Work as a team',
      text: 'Every person gets only the workspace and permissions their role needs.',
    },
    {
      title: 'Every channel',
      text: 'Web, social, marketplace and physical sales remain connected.',
    },
    {
      title: 'Ready to grow',
      text: 'Add a branch, warehouse, team, language or country on the same core.',
    },
    {
      title: 'Always evolving',
      text: 'New capabilities reach the product without forcing another system.',
    },
  ],
};
const PRICING_ITEMS: Record<string, Array<{ title: string; text: string }>> = {
  tr: [
    {
      title: 'İhtiyacın olan yerden başla',
      text: 'Önce iş modelini ve bugün kullanacağın ürünleri seç.',
    },
    {
      title: 'Aynı sistemde genişle',
      text: 'Yeni şube, ekip, satış kanalı veya ülke için altyapı değiştirme.',
    },
    {
      title: 'Özel işlere açık',
      text: 'Hazır kapsamın yetmediği operasyonlarda modüler özel çözüm oluştur.',
    },
  ],
  en: [
    {
      title: 'Start where you need',
      text: 'Choose the business model and products you use today.',
    },
    {
      title: 'Expand on the same system',
      text: 'Do not change infrastructure for a new team, channel or country.',
    },
    {
      title: 'Open to custom work',
      text: 'Build a modular custom solution when the ready scope is not enough.',
    },
  ],
};

type HubDepth = {
  eyebrow: string;
  title: string;
  lead: string;
  sections: Array<{ title: string; text: string; points: string[] }>;
  faqEyebrow: string;
  faqTitle: string;
  faqs: Array<{ question: string; answer: string }>;
};

const HUB_DEPTH: Record<string, Record<HubKind, HubDepth>> = {
  tr: {
    why: {
      eyebrow: 'Bir özellik listesi değil',
      title: 'İşin bir ucunda olan, diğer ucunda kaybolmaz.',
      lead: 'Aymudo’nun farkı tek tek araçların sayısı değil; müşteri, ekip ve operasyon bilgisinin aynı işin devamı olarak hareket etmesidir.',
      sections: [
        {
          title: 'Müşteriyi her temasında tanı',
          text: 'Bir ziyaretçinin formu, mesajı, randevusu, siparişi ve sonraki talebi birbirinden kopuk kayıtlar olarak kalmaz.',
          points: [
            'Ortak müşteri geçmişi',
            'Randevu ve sipariş bağlamı',
            'Tekrarlanan veri girişini azaltan akış',
          ],
        },
        {
          title: 'Ekibe doğru çalışma alanını ver',
          text: 'Her çalışan tüm paneli öğrenmek zorunda kalmaz. Rolüne, şubesine ve sorumluluğuna göre yalnızca gerekli ekranla çalışır.',
          points: ['Rol ve ekran atama', 'Şube bazlı yetki', 'Tek staff kimliğiyle çoklu iş yeri'],
        },
        {
          title: 'Modüler başla, kopuk büyüme',
          text: 'Bugün yalnızca randevu veya e-ticaretle başlayın. Yarın POS, kampanya ya da pazaryeri eklediğinizde veriyi başka sisteme taşımayın.',
          points: [
            'Ortak ürün ve müşteri verisi',
            'Birlikte çalışan modüller',
            'İhtiyaca göre açılan kapsam',
          ],
        },
        {
          title: 'Yeni ölçeğe aynı omurgayla geç',
          text: 'Yeni şube, depo, ekip, dil, ülke ve satış kanalı büyümenin doğal parçası olur; yeniden platform kurma projesine dönüşmez.',
          points: ['Çoklu şube ve depo', '15 dil ve RTL', 'Yaklaşık 30 güncel para birimi'],
        },
      ],
      faqEyebrow: 'Merak edilenler',
      faqTitle: 'Aymudo işinize nasıl yerleşir?',
      faqs: [
        {
          question: 'Aymudo yalnızca e-ticaret sistemi mi?',
          answer:
            'Hayır. E-ticaret Aymudo ürünlerinden biridir. Randevu, şube, personel, içerik, POS, pazaryeri, otomasyon ve müşteri iletişimi aynı platformun parçalarıdır.',
        },
        {
          question: 'Bütün ürünleri ilk günden kullanmak gerekir mi?',
          answer:
            'Hayır. İşletmeniz bugün neye ihtiyaç duyuyorsa onunla başlayabilir, yeni ihtiyaçları aynı altyapı üzerinde sonradan açabilirsiniz.',
        },
        {
          question: 'Mevcut işletmem Aymudo’ya taşınabilir mi?',
          answer:
            'Evet. Geçiş; mevcut veri, yayın, ekip ve operasyon yapısına göre aşamalı planlanabilir. Amaç işi durdurmadan yeni sisteme yerleşmektir.',
        },
        {
          question: 'Standart akışın dışında ihtiyaçlarımız varsa?',
          answer:
            'Aymudo modüler yapısıyla işletmeye göre şekillenebilir. Hazır kapsamın dışındaki operasyonlar için özel akış ve entegrasyonlar değerlendirilebilir.',
        },
      ],
    },
    products: {
      eyebrow: 'Gerçek çalışma biçimleri',
      title: 'Ürünler tek başına değil, senaryonun içinde değer üretir.',
      lead: 'Her Aymudo ürünü bağımsız bir ihtiyacı çözer; aynı zamanda müşteriden ödemeye, ekipten teslimata uzanan ortak bir akışa bağlanır.',
      sections: [
        {
          title: 'Klinik ve hizmet işletmesi',
          text: 'Müşteri uygun saati görür, hizmet ve personel seçer; şube takvimi güncellenir, ekip kendi ekranından süreci takip eder.',
          points: [
            'Randevu ve kaynak uygunluğu',
            'Şube ve personel yönetimi',
            'Hatırlatma ve müşteri iletişimi',
          ],
        },
        {
          title: 'Çok kanallı perakende',
          text: 'Web, sosyal medya ve mağazadaki satışlar aynı ürün, varyasyon ve stok gerçeğine bağlanır.',
          points: ['E-ticaret ve ödeme', 'Depo ve barkod', 'POS, adisyon ve kasa'],
        },
        {
          title: 'Pazaryeri işletmesi',
          text: 'Farklı satıcılar kendi ürün ve siparişleriyle çalışırken platform; komisyonu, müşteriyi ve genel operasyonu yönetir.',
          points: [
            'Satıcı çalışma alanları',
            'Ürün ve sipariş akışı',
            'Komisyon ve platform kontrolü',
          ],
        },
        {
          title: 'Global marka',
          text: 'Tek içerik ve ürün merkezi; ziyaretçinin dili, ülkesi ve para birimine göre yerelleşen deneyimlere dönüşür.',
          points: [
            '15 dil ve Arapça RTL',
            'Yaklaşık 30 para birimi',
            'Ülke, depo ve kargo profilleri',
          ],
        },
      ],
      faqEyebrow: 'Ürün mimarisi',
      faqTitle: 'Birlikte çalışmak ne anlama geliyor?',
      faqs: [
        {
          question: 'Ürünler ayrı ayrı kullanılabilir mi?',
          answer:
            'Evet. İhtiyacınız olan ürünle başlayabilirsiniz. Ancak ortak müşteri, ekip ve operasyon verisi sayesinde yeni bir ürün eklediğinizde sıfırdan bağlantı kurmanız gerekmez.',
        },
        {
          question: 'Tema ve içerik gerçekten panelden yönetiliyor mu?',
          answer:
            'Evet. Tema motoru yalnızca renk değiştirme aracı değildir; bölümler, görseller, metinler ve sayfa davranışları yönetim panelinden markaya göre düzenlenebilir.',
        },
        {
          question: 'Bir personel birden fazla iş yerinde çalışabilir mi?',
          answer:
            'Evet. Tek staff kimliği uygun yetkilerle birden fazla işletme veya çalışma alanına bağlanabilir.',
        },
        {
          question: 'Özel entegrasyon ve iş akışı eklenebilir mi?',
          answer:
            'Aymudo’nun ürünleri modüler bir çekirdek üzerinde çalışır. İşletmenin kapsamına göre özel akışlar, ekranlar ve entegrasyonlar planlanabilir.',
        },
      ],
    },
    pricing: {
      eyebrow: 'Abonelik nasıl şekillenir?',
      title: 'Fiyatı gereksiz paketler değil, çalıştırdığınız sistem belirler.',
      lead: 'Önce iş modelini ve gerçek ihtiyacı netleştiririz. Kapsam; kullanılan ürünler, operasyon ölçeği ve gerekiyorsa özel geliştirmeler üzerinden anlaşılır biçimde oluşur.',
      sections: [
        {
          title: 'İş modeliniz',
          text: 'Klinik, mağaza, restoran, pazaryeri veya hizmet işletmesinin başlangıç ihtiyaçları aynı değildir.',
          points: ['İşletme türü', 'Müşteri yolculuğu', 'Temel operasyon akışı'],
        },
        {
          title: 'Kullandığınız ürünler',
          text: 'Randevu, e-ticaret, POS, tema motoru veya otomasyon gibi bugün kullanacağınız ürünler seçilir.',
          points: ['Gerekli modüller', 'Yayın ve satış kanalları', 'Yönetim ihtiyaçları'],
        },
        {
          title: 'Operasyon ölçeği',
          text: 'Şube, personel, depo, ülke ve satış kanalı yapısı sistemin nasıl kurulacağını belirler.',
          points: ['Ekip ve yetki yapısı', 'Şube ve depo sayısı', 'Dil ve ülke kapsamı'],
        },
        {
          title: 'Özel ihtiyaçlar',
          text: 'Hazır ürünlerin dışında kalan entegrasyon, özel ekran veya operasyon akışları ayrıca planlanır.',
          points: ['Özel entegrasyonlar', 'Veri geçişi', 'Kurulum ve destek kapsamı'],
        },
      ],
      faqEyebrow: 'Fiyatlandırma SSS',
      faqTitle: 'Başlamadan önce bilmeniz gerekenler.',
      faqs: [
        {
          question: 'Abonelik fiyatı nasıl belirleniyor?',
          answer:
            'Fiyat; işletme modeli, kullanılacak Aymudo ürünleri, operasyon ölçeği ve varsa özel ihtiyaçların birlikte değerlendirilmesiyle oluşur.',
        },
        {
          question: 'Sonradan yeni ürün ekleyebilir miyim?',
          answer:
            'Evet. Aymudo’nun temel avantajlarından biri, yeni ihtiyacı aynı altyapıya ekleyerek büyüyebilmenizdir.',
        },
        {
          question: 'Mevcut verilerin aktarımı fiyata dahil mi?',
          answer:
            'Veri geçişinin kapsamı işletmeden işletmeye değişir. Mevcut sistem, veri hacmi ve aktarılacak kayıtlar incelendikten sonra açık biçimde planlanır.',
        },
        {
          question: 'Ne zaman özel teklif gerekir?',
          answer:
            'Çoklu ülke operasyonu, kapsamlı veri geçişi, özel entegrasyon veya hazır ürünlerin dışındaki iş akışlarında özel kapsam hazırlanır.',
        },
      ],
    },
  },
  en: {
    why: {
      eyebrow: 'More than a feature list',
      title: 'What happens at one end of the business is not lost at the other.',
      lead: 'Aymudo is not defined by the number of tools. Its value comes from customer, team and operational context moving through one connected business.',
      sections: [
        {
          title: 'Know the customer at every touchpoint',
          text: 'Forms, messages, bookings, orders and later requests become one useful history instead of isolated records.',
          points: [
            'Shared customer history',
            'Booking and order context',
            'Less repeated data entry',
          ],
        },
        {
          title: 'Give the team the right workspace',
          text: 'People do not need to learn an entire back office. Each role works with the screens, branch and authority it needs.',
          points: [
            'Role-based screens',
            'Branch permissions',
            'One staff identity across workplaces',
          ],
        },
        {
          title: 'Start modular, grow connected',
          text: 'Begin with booking or commerce. Add POS, campaigns or a marketplace later without moving the business to another system.',
          points: [
            'Shared product and customer data',
            'Products designed to connect',
            'Scope that opens as needed',
          ],
        },
        {
          title: 'Move to the next scale on the same core',
          text: 'A new branch, warehouse, team, language, country or channel becomes an extension—not a replacement project.',
          points: [
            'Multiple branches and warehouses',
            '15 languages and RTL',
            'Nearly 30 live currencies',
          ],
        },
      ],
      faqEyebrow: 'Common questions',
      faqTitle: 'How does Aymudo fit the business?',
      faqs: [
        {
          question: 'Is Aymudo only an ecommerce platform?',
          answer:
            'No. Commerce is one Aymudo product. Booking, branches, staff, content, POS, marketplaces, automation and communication are parts of the same platform.',
        },
        {
          question: 'Do we need every product from day one?',
          answer:
            'No. Start with what the business needs today and activate new capabilities on the same infrastructure later.',
        },
        {
          question: 'Can an existing operation move to Aymudo?',
          answer:
            'Yes. Migration can be phased around existing data, publishing, team and operational needs so the business can keep working.',
        },
        {
          question: 'What if our workflow is not standard?',
          answer:
            'Aymudo is modular and can adapt to the business. Custom workflows and integrations can be scoped when the ready product is not enough.',
        },
      ],
    },
    products: {
      eyebrow: 'Real ways of working',
      title: 'Products create value inside a workflow, not in isolation.',
      lead: 'Each Aymudo product solves a clear need and connects to a shared journey from customer and team to payment and delivery.',
      sections: [
        {
          title: 'Clinic and service business',
          text: 'A customer finds availability, chooses a service and professional, while the branch calendar and team workspace update together.',
          points: [
            'Booking and resource availability',
            'Branches and staff',
            'Reminders and communication',
          ],
        },
        {
          title: 'Omnichannel retail',
          text: 'Web, social and in-store sales use the same product, variant and inventory truth.',
          points: ['Commerce and payments', 'Warehouse and barcode', 'POS, tabs and till'],
        },
        {
          title: 'Marketplace operation',
          text: 'Sellers manage their products and orders while the platform controls commissions, customers and the overall operation.',
          points: [
            'Seller workspaces',
            'Product and order flow',
            'Commission and platform control',
          ],
        },
        {
          title: 'Global brand',
          text: 'One content and product centre becomes a localised experience for each visitor, country and currency.',
          points: [
            '15 languages and Arabic RTL',
            'Nearly 30 currencies',
            'Country, warehouse and shipping profiles',
          ],
        },
      ],
      faqEyebrow: 'Product architecture',
      faqTitle: 'What does working together mean?',
      faqs: [
        {
          question: 'Can products be used separately?',
          answer:
            'Yes. Start with the product you need. Shared customer, team and operational data means a new product does not require a new integration project.',
        },
        {
          question: 'Is the theme and content really managed in the panel?',
          answer:
            'Yes. The theme engine goes beyond colours: sections, media, copy and page behaviour can be managed around the brand.',
        },
        {
          question: 'Can one staff member work across businesses?',
          answer:
            'Yes. One staff identity can connect to multiple businesses or workspaces with the appropriate permissions.',
        },
        {
          question: 'Can custom integrations be added?',
          answer:
            'Aymudo products run on a modular core. Custom workflows, screens and integrations can be planned around the operation.',
        },
      ],
    },
    pricing: {
      eyebrow: 'How the subscription takes shape',
      title: 'Your working system—not unused bundles—defines the scope.',
      lead: 'We start with the business model and real needs. The products in use, operational scale and any custom work form a clear subscription scope.',
      sections: [
        {
          title: 'Your business model',
          text: 'A clinic, shop, restaurant, marketplace and service business do not need the same starting point.',
          points: ['Type of business', 'Customer journey', 'Core operational flow'],
        },
        {
          title: 'Products in use',
          text: 'Choose the capabilities you need today, such as booking, commerce, POS, themes or automation.',
          points: ['Required modules', 'Publishing and sales channels', 'Management needs'],
        },
        {
          title: 'Operational scale',
          text: 'Branches, staff, warehouses, countries and channels shape how the system is configured.',
          points: ['Team and permissions', 'Branches and warehouses', 'Language and country scope'],
        },
        {
          title: 'Custom needs',
          text: 'Integrations, special screens or workflows beyond the ready products are scoped separately.',
          points: ['Custom integrations', 'Data migration', 'Setup and support scope'],
        },
      ],
      faqEyebrow: 'Pricing FAQ',
      faqTitle: 'What to know before you start.',
      faqs: [
        {
          question: 'How is the subscription price determined?',
          answer:
            'The business model, selected Aymudo products, operational scale and any custom requirements are evaluated together.',
        },
        {
          question: 'Can I add products later?',
          answer:
            'Yes. A core Aymudo advantage is expanding on the same infrastructure as new needs appear.',
        },
        {
          question: 'Is existing data migration included?',
          answer:
            'Migration differs by business. The current system, data volume and records to move are reviewed and scoped clearly.',
        },
        {
          question: 'When is a custom proposal needed?',
          answer:
            'Multi-country operations, extensive migration, custom integrations or workflows outside the ready products require a tailored scope.',
        },
      ],
    },
  },
};
